import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import React, { useCallback, useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import LaunchIcon from "@material-ui/icons/Launch";
import { useHistory } from "react-router-dom";
import moment from "moment/moment";

interface IAssignmentSummaryBox {
  busSchedulePlanId: number;
  busScheduleType: string;
  startDate: string;
  endDate: string;
}

const useStyles = makeStyles({
  paper: {
    padding: "6px 16px",
    height: "100%",
    "& div > div:first-child": {
      minHeight: "60px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  },
  subTitle: {
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  description: {
    fontSize: "34px",
  },
});

const AssignmentSummaryBox = ({
  busSchedulePlanId,
  busScheduleType,
  startDate,
  endDate,
}: IAssignmentSummaryBox) => {
  const [assignmentSummary, setAssignmentSummary] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const classes = useStyles();
  const { customGetListWithStaticParams } = useDataProvider();
  const history = useHistory();

  const getAssignmentSummary = useCallback(
    async (busSchedulePlanId: number, startDate: string, endDate) => {
      try {
        const assignmentSummaryResponse = await customGetListWithStaticParams(
          `summaries/assignment?BusSchedulePlanId=${busSchedulePlanId}&StartDate=${startDate}&EndDate=${endDate}`,
        );

        setAssignmentSummary(assignmentSummaryResponse.data);
        setIsLoading(false);
        setIsLoaded(true);
      } catch (error) {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (busSchedulePlanId && startDate && endDate) {
      setIsLoading(true);
      setIsLoaded(false);
      getAssignmentSummary(busSchedulePlanId, startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busSchedulePlanId, startDate, endDate]);

  return (
    <>
      {isLoading ? (
        <div>
          <CircularProgress />
        </div>
      ) : (
        <>
          {isLoaded ? (
            <SanitizedGrid
              container
              spacing={10}
              style={{ justifyContent: "center" }}
            >
              <Grid item xs={12} md={6} lg={3}>
                <Paper className={classes.paper}>
                  <SanitizedGrid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        className={classes.subTitle}
                        variant="h6"
                        align="center"
                      >
                        <span>Total Assignments</span>
                        <IconButton
                          onClick={() =>
                            history.push(
                              `/assignments?filter=${JSON.stringify({
                                BusSchedulePlanId: busSchedulePlanId,
                                EstimatedStartedAt:
                                  moment(startDate).format("Y-MM-DD"),
                              })}`,
                            )
                          }
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        className={classes.description}
                        variant="h6"
                        gutterBottom
                        align="center"
                      >
                        {assignmentSummary.totalTrip}
                      </Typography>
                    </Grid>
                  </SanitizedGrid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Paper className={classes.paper}>
                  <SanitizedGrid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        className={classes.subTitle}
                        variant="h6"
                        align="center"
                      >
                        <span>Assignments Not Started</span>
                        <IconButton
                          onClick={() =>
                            history.push(
                              `/assignments?filter=${JSON.stringify({
                                BusSchedulePlanId: busSchedulePlanId,
                                EstimatedStartedAt:
                                  moment(startDate).format("Y-MM-DD"),
                                Status: "Ready",
                              })}`,
                            )
                          }
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        className={classes.description}
                        variant="h6"
                        gutterBottom
                        align="center"
                      >
                        {assignmentSummary.tripReady}
                      </Typography>
                    </Grid>
                  </SanitizedGrid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Paper className={classes.paper}>
                  <SanitizedGrid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        className={classes.subTitle}
                        variant="h6"
                        align="center"
                      >
                        <span>Assignments Running</span>
                        <IconButton
                          onClick={() =>
                            history.push(
                              `/assignments?filter=${JSON.stringify({
                                BusSchedulePlanId: busSchedulePlanId,
                                EstimatedStartedAt:
                                  moment(startDate).format("Y-MM-DD"),
                                Status: "Started",
                              })}`,
                            )
                          }
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        className={classes.description}
                        variant="h6"
                        gutterBottom
                        align="center"
                      >
                        {assignmentSummary.tripStarted}
                      </Typography>
                    </Grid>
                  </SanitizedGrid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Paper className={classes.paper}>
                  <SanitizedGrid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        className={classes.subTitle}
                        variant="h6"
                        align="center"
                      >
                        <span>Assignments Ended</span>
                        <IconButton
                          onClick={() =>
                            history.push(
                              `/assignments?filter=${JSON.stringify({
                                BusSchedulePlanId: busSchedulePlanId,
                                EstimatedStartedAt:
                                  moment(startDate).format("Y-MM-DD"),
                                MultipleStatus: [
                                  "Ended",
                                  "ForceEnded",
                                  "EndedByAdmin",
                                ],
                              })}`,
                            )
                          }
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        className={classes.description}
                        variant="h6"
                        gutterBottom
                        align="center"
                      >
                        {assignmentSummary.tripEnded}
                      </Typography>
                    </Grid>
                  </SanitizedGrid>
                </Paper>
              </Grid>

              {busScheduleType === "to_school" && (
                <>
                  <Grid item xs={12} md={6} lg={3}>
                    <Paper className={classes.paper}>
                      <SanitizedGrid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography
                            className={classes.subTitle}
                            variant="h6"
                            align="center"
                          >
                            Bus On Time
                            <IconButton
                              onClick={() =>
                                history.push(
                                  `/assignments?filter=${JSON.stringify({
                                    BusSchedulePlanId: busSchedulePlanId,
                                    EstimatedStartedAt:
                                      moment(startDate).format("Y-MM-DD"),
                                    ArrivalStatus: "BusOnTime",
                                  })}`,
                                )
                              }
                            >
                              <LaunchIcon />
                            </IconButton>
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography
                            className={classes.description}
                            variant="h6"
                            gutterBottom
                            align="center"
                          >
                            {assignmentSummary.busOnTime}
                          </Typography>
                        </Grid>
                      </SanitizedGrid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6} lg={3}>
                    <Paper className={classes.paper}>
                      <SanitizedGrid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography
                            className={classes.subTitle}
                            variant="h6"
                            align="center"
                          >
                            Bus Late
                            <IconButton
                              onClick={() =>
                                history.push(
                                  `/assignments?filter=${JSON.stringify({
                                    BusSchedulePlanId: busSchedulePlanId,
                                    EstimatedStartedAt:
                                      moment(startDate).format("Y-MM-DD"),
                                    ArrivalStatus: "BusLate",
                                  })}`,
                                )
                              }
                            >
                              <LaunchIcon />
                            </IconButton>
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography
                            className={classes.description}
                            variant="h6"
                            gutterBottom
                            align="center"
                          >
                            {assignmentSummary.busLate}
                          </Typography>
                        </Grid>
                      </SanitizedGrid>
                    </Paper>
                  </Grid>
                </>
              )}
            </SanitizedGrid>
          ) : (
            <></>
          )}
        </>
      )}
    </>
  );
};

export default AssignmentSummaryBox;
